// Subscribes to the `sam:action` socket envelope and routes each action to a callback. This is a
// pure socket→callback bridge: the chat workspace (useSamWorkspace) supplies the handlers and owns
// all state. Re-delivered envelopes are deduped by (message_id + action payload) so an identical
// envelope isn't applied twice — but the *distinct* envelopes a single turn emits still all apply
// (every publish_action in a turn reuses the turn's message_id, so a panel-open and a later
// REFRESH share it; keying on message_id alone would silently drop the REFRESH).
import { useEffect, useRef } from 'react';
import { socketService } from '@/services/SocketService';
import {
  CLOSE_CARD,
  CONFIRMATION,
  CONFIRMATION_RESULT,
  DRAFT_COMMUNICATION,
  EXECUTE_NOTE_FOLLOWUPS,
  SET_NOTE_FOLLOWUP_INCLUDED,
  EXPAND_NOTE_FOLLOWUP,
  NAVIGATE_CALENDAR,
  NAVIGATE_CARDS,
  NAVIGATE_ROUTE,
  OPEN_CARD,
  OPEN_NOTE_TAKER,
  STOP_NOTE_TAKER,
  REFRESH_TASK_COMMENTS,
  REFRESH_TASK_DETAIL,
  REFRESH_MEETING_DETAIL,
  REFRESH_PROPERTY_DETAIL,
  SWITCH_MEETING_TAB,
  SWITCH_TASK_TAB,
  SWITCH_VIEW,
  OPEN_WORKFLOW_SETTINGS_PANEL,
  REFRESH_WORKFLOW_SETTINGS,
  OPEN_COMPANY_WORKFLOWS_PANEL,
  REPLAY_RECENT_VIEW,
  type SamAction,
  type SamActionEnvelope,
  type SamFacet,
  type SamModule,
} from '@/components/SAM/samActionTypes';

// Workflow ('workflow' module) is a single-object surface (WorkflowPanel/WorkflowCard) — NOT a
// list rendered through the generic InlineCards. OPEN_WORKFLOW_PANEL and the reused
// OPEN_ENGAGEMENTS_PANEL (module: 'workflow', from list_pending_approvals) both end in `_PANEL`,
// so without this check they'd fall into the generic onApplyFilter/renderModuleCards (list) path
// below — which produces a `CardsMessage`, not the `WorkflowCardMessage` this surface needs. Same
// for REFRESH_WORKFLOW vs. the generic REFRESH_<module> handling. 'engagements' (a genuine list of
// Engagement records, from search_engagement) is NOT special-cased here and flows through the
// generic list pipeline unchanged.
//
// OPEN_WORKFLOW_SETTINGS_PANEL / REFRESH_WORKFLOW_SETTINGS also reuse module: 'workflow' (a
// SECOND, DISTINCT single-object surface — the tenant's whole workflow STRUCTURE, no engagement),
// and both also end in `_PANEL` / start with `REFRESH_` — so module+suffix alone can't tell them
// apart from OPEN_WORKFLOW_PANEL/REFRESH_WORKFLOW. Every branch below checks the literal
// action_type string first, so the settings pair is intercepted before the generic engagement pair.
const isWorkflowModuleAction = (a: SamAction) => a.module === 'workflow';

interface UseSamActionsOptions {
  /** Called when SAM emits a guardrailed-tool confirmation card. */
  onConfirmation?: (action: SamAction) => void;
  /** Called when a guardrailed tool's execution finishes — reports the real outcome (success/
   *  failed/cancelled) for the confirmation card matching `call_id`. */
  onConfirmationResult?: (action: SamAction) => void;
  /**
   * SAM activated a module with a filter (OPEN_*_PANEL / APPLY_*_FILTER). The socket carries only
   * {module, filter, facets, title, caption, record_id} — the UI fetches the rows itself via REST.
   * title/caption (LLM-authored) head the workspace view + sit behind its "⋯" menu (the mandatory
   * display). `recordId` is the record a detail-opening read is bound to (e.g. get_engagement); it
   * is what makes the client's "⋯" entry key match the one publish_action recorded server-side.
   * `view` carries the menu-ONLY labels (view_title/view_caption) a write can attach to name its
   * "⋯" entry after the record it just created — kept apart from title/caption so it never spawns
   * an inline chat card, and so the live entry reads the same as the persisted one.
   */
  onApplyFilter?: (module: SamModule, filter: Record<string, unknown>, facets?: SamFacet[], title?: string, caption?: string, messageId?: string, recordId?: string | null, view?: { title?: string; caption?: string }) => void;
  /** A REFRESH_<module> action — re-fetch the module with its last filter. messageId is the
   *  write turn's id, so the refreshed card can re-point its reply strip to THIS turn's response. */
  onRefresh?: (module: SamModule, messageId?: string) => void;
  /**
   * A list/panel read carried an LLM-authored title/caption — optionally ALSO append an inline
   * "action card" to the chat thread (gated by config). Fires once per module per envelope.
   */
  onActionCard?: (module: SamModule, filter: Record<string, unknown>, title?: string, caption?: string, recordId?: string | null) => void;
  /**
   * Navigate to a card. recordId: open by id; index: open by 0-based position; recordId=null =
   * close detail. module may be undefined on a generic close → close whichever card is open.
   */
  onOpenCard?: (module: SamModule | undefined, recordId: string | null, index?: number) => void;
  /** SWITCH_VIEW — flip the surface between the chat thread and the workspace/cards. */
  onSwitchView?: (target: 'chat' | 'workspace') => void;
  /** NAVIGATE_CARDS — hands-free swipe: step 'next'/'prev', or jump to a 1-based position. */
  onNavigateCards?: (opts: { direction?: 'next' | 'prev'; position?: number }) => void;
  /** OPEN_NOTE_TAKER — open the note-capture card in the workspace and start recording. */
  onOpenNoteTaker?: (opts?: { contextType?: string; entityId?: string }) => void;
  /** STOP_NOTE_TAKER — stop the active note recording (SAM stop_note_taker). */
  onStopNoteTaker?: () => void;
  /** EXECUTE_NOTE_FOLLOWUPS — trigger "Create all selected" on the open note review card. */
  onExecuteNoteFollowups?: () => void;
  /** SET_NOTE_FOLLOWUP_INCLUDED — check/uncheck one, several, or all follow-up suggestions. */
  onSetNoteFollowupIncluded?: (opts: { suggestionId?: string; suggestionIds?: string[]; included: boolean }) => void;
  /** EXPAND_NOTE_FOLLOWUP — expand a follow-up's review/edit panel, optionally prefilling fields. */
  onExpandNoteFollowup?: (opts: { suggestionId: string; fields?: Record<string, unknown> }) => void;
  /** DRAFT_COMMUNICATION — SAM drafted (or live-edited) an email/SMS/in-app message for the user to
   *  review + send. messageId is the turn id, so the draft card can show the matching reply. */
  onDraftCommunication?: (action: SamAction, messageId?: string) => void;
  /** NAVIGATE_ROUTE — push a frontend route (e.g. '/tasks/new', '/tasks/:id'). */
  onNavigateRoute?: (path: string) => void;
  /** SWITCH_TASK_TAB — switch the active tab in the open task detail panel. */
  onSwitchTaskTab?: (tab: string) => void;
  /** SWITCH_MEETING_TAB — switch the active tab in the open meeting detail panel. */
  onSwitchMeetingTab?: (tab: string) => void;
  /** REFRESH_TASK_COMMENTS — invalidate RTK Query cache for a task's comments. */
  onRefreshTaskComments?: (taskId: string) => void;
  /** REFRESH_TASK_DETAIL — invalidate RTK Query cache for a specific task (after update_task). */
  onRefreshTaskDetail?: (taskId: string) => void;
  /** REFRESH_MEETING_DETAIL — invalidate RTK Query cache for a specific meeting (after
   *  reschedule_meeting / cancel_meeting / add_meeting_note). */
  onRefreshMeetingDetail?: (meetingId: string) => void;
  /** REFRESH_PROPERTY_DETAIL — invalidate RTK Query cache for a specific property (after
   *  execute_workflow_transition changes its active engagement's stage/status). */
  onRefreshPropertyDetail?: (propertyId: string) => void;
  /** NAVIGATE_CALENDAR — move or reframe the on-screen calendar workspace. */
  onNavigateCalendar?: (opts: { direction?: string; unit?: string; view?: string; date?: string }) => void;
  /** OPEN_WORKFLOW_PANEL, or OPEN_ENGAGEMENTS_PANEL with module 'workflow' (list_pending_approvals'
   *  approvals-only view) — (re-)open the single Workflow surface, optionally bound to an
   *  engagement (record_id). messageId links it to the turn's assistant reply. */
  onOpenWorkflowPanel?: (engagementId: string | undefined, title: string | undefined, caption: string | undefined, messageId: string | undefined) => void;
  /** REFRESH_WORKFLOW — invalidate the open Workflow surface's state/transitions/approvals cache
   *  (execute_workflow_transition / decide_workflow_approval hit the workflow service directly,
   *  bypassing the frontend's own RTK mutations). */
  onRefreshWorkflow?: (engagementId: string | undefined) => void;
  /** OPEN_WORKFLOW_SETTINGS_PANEL (get_workflow_customization) — (re-)open the Workflow SETTINGS
   *  surface (the tenant's whole workflow STRUCTURE editor; no engagement scope). messageId links
   *  it to the turn's assistant reply. */
  onOpenWorkflowSettingsPanel?: (title: string | undefined, caption: string | undefined, messageId: string | undefined) => void;
  /** REFRESH_WORKFLOW_SETTINGS — invalidate the open Workflow Settings surface's cache (the
   *  customization mutation tools hit the customization service directly, bypassing the frontend's
   *  own RTK mutations). */
  onRefreshWorkflowSettings?: () => void;
  /** OPEN_COMPANY_WORKFLOWS_PANEL (list_company_workflows) — open the company processes overview
   *  list card. The user clicks "Customize" on the card to open the full settings editor. */
  onOpenCompanyWorkflowsPanel?: (title: string | undefined, caption: string | undefined, messageId: string | undefined) => void;
  /** REPLAY_RECENT_VIEW (list_recent_views / open_recent_view) — reopen a "⋯" menu entry the
   *  agent picked, by reusing the SAME dispatch `replayView` already applies to a user's own menu
   *  click: module + filter + an optional bound record, plus the row's own title/caption. */
  onReplayRecentView?: (module: SamModule, filter: Record<string, unknown>, recordId: string | null, title?: string, caption?: string, contextRefType?: string) => void;
}

export function useSamActions(options: UseSamActionsOptions = {}) {
  const optsRef = useRef(options);
  optsRef.current = options;
  const seenRef = useRef<Set<string>>(new Set());
  // message_ids whose turn opened/refreshed the engagement Workflow surface. Defense-in-depth
  // against SAM incidentally calling an unrelated read tool (get_meetings, get_tasks, ...) in the
  // SAME turn as a workflow action — without this, that read's OPEN_*_PANEL steals the workspace
  // surface from the engagement Workflow card the user was just looking at (see
  // useChatWorkspace's `latestSurface`, which is simply "whichever surface message is newest").
  // A turn that ONLY touches another module (a genuine standalone request) is never added here, so
  // it still switches the surface normally.
  const workflowTurnIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    // Card navigation (open/close) — handled here so a generic close works even with no module.
    const routeNavigation = (a: SamAction) => {
      const t = a.action_type || '';
      const module = a.module as SamModule | undefined;
      if (t === CLOSE_CARD || t === 'BACK' || t === 'BACK_TO_LIST' || t.includes('CLOSE_CARD')) {
        optsRef.current.onOpenCard?.(module, null);
        return;
      }
      if (!module) return;
      if (t === OPEN_CARD || t.includes('OPEN_CARD') || t.includes('DETAIL')) {
        optsRef.current.onOpenCard?.(module, a.record_id ?? null, a.index);
        // If the action carries a tab (e.g. OPEN_MEETING_DETAIL with tab="activity"), switch it now.
        if (t.includes('MEETING') && a.tab) optsRef.current.onSwitchMeetingTab?.(a.tab);
      }
    };

    const onAction = (raw: unknown) => {
      const env = raw as SamActionEnvelope;

      if (env.action && env.action.action_type === CONFIRMATION) {
        optsRef.current.onConfirmation?.(env.action);
        return;
      }
      if (env.action && env.action.action_type === CONFIRMATION_RESULT) {
        optsRef.current.onConfirmationResult?.(env.action);
        return;
      }

      const actions = env.actions ?? (env.action ? [env.action] : []);
      if (!actions.length) return;

      // Dedup on the envelope content, not just the turn id: message_id is per-turn (reused by
      // every publish_action in the turn), so a panel-open + a later REFRESH share it. Keying on
      // message_id + the actions payload drops true re-deliveries while letting each distinct
      // envelope in a turn through (so a write's REFRESH actually re-fetches the cards).
      const mid = env.message_id;
      if (mid) {
        const sig = `${mid}|${JSON.stringify(actions)}`;
        if (seenRef.current.has(sig)) return;
        seenRef.current.add(sig);
        if (seenRef.current.size > 200) seenRef.current = new Set([sig]);
      }

      // A read tool emits OPEN_*_PANEL + APPLY_*_FILTER together — trigger the fetch once per module.
      const applied = new Set<string>();
      for (const a of actions) {
        const t = a.action_type || '';
        // View-control actions carry no module/data — handle and skip the module/filter logic.
        if (t === SWITCH_VIEW) {
          if (a.target === 'chat' || a.target === 'workspace') optsRef.current.onSwitchView?.(a.target);
          continue;
        }
        if (t === NAVIGATE_CARDS) {
          optsRef.current.onNavigateCards?.({ direction: a.direction as 'next' | 'prev' | undefined, position: a.position });
          continue;
        }
        if (t === OPEN_NOTE_TAKER) {
          optsRef.current.onOpenNoteTaker?.({ contextType: a.context_type, entityId: a.entity_id });
          continue;
        }
        if (t === STOP_NOTE_TAKER) {
          optsRef.current.onStopNoteTaker?.();
          continue;
        }
        if (t === EXECUTE_NOTE_FOLLOWUPS) {
          optsRef.current.onExecuteNoteFollowups?.();
          continue;
        }
        if (t === SET_NOTE_FOLLOWUP_INCLUDED) {
          optsRef.current.onSetNoteFollowupIncluded?.({
            suggestionId: a.suggestion_id, suggestionIds: a.suggestion_ids, included: a.included ?? true,
          });
          continue;
        }
        if (t === EXPAND_NOTE_FOLLOWUP) {
          if (a.suggestion_id) optsRef.current.onExpandNoteFollowup?.({ suggestionId: a.suggestion_id, fields: a.fields });
          continue;
        }
        if (t === DRAFT_COMMUNICATION) {
          optsRef.current.onDraftCommunication?.(a, mid);
          continue;
        }
        // Reopen a "⋯" recent-views entry — module-agnostic, checked before the generic
        // PANEL/FILTER handling below since this carries its own module but isn't a list/panel
        // read in the usual sense (no facets, no row fetch triggered from here).
        if (t === REPLAY_RECENT_VIEW && a.module) {
          optsRef.current.onReplayRecentView?.(a.module, a.filter ?? {}, a.record_id ?? null, a.title, a.caption, a.context_ref_type);
          continue;
        }
        // Company Processes list card — checked before OPEN_WORKFLOW_SETTINGS_PANEL (both are
        // workflow-module actions but this one is the overview step, not the editor).
        if (t === OPEN_COMPANY_WORKFLOWS_PANEL) {
          optsRef.current.onOpenCompanyWorkflowsPanel?.(a.title, a.caption, mid);
          continue;
        }
        // Workflow SETTINGS single-object surface — checked by literal action_type BEFORE the
        // generic workflow-engagement pair below (both reuse module: 'workflow' and the
        // `_PANEL`/`REFRESH_` suffix pattern, so module+suffix alone can't tell them apart).
        if (t === OPEN_WORKFLOW_SETTINGS_PANEL) {
          optsRef.current.onOpenWorkflowSettingsPanel?.(a.title, a.caption, mid);
          continue;
        }
        if (t === REFRESH_WORKFLOW_SETTINGS) {
          optsRef.current.onRefreshWorkflowSettings?.();
          continue;
        }
        // Workflow (engagement) single-object surface — intercept before the generic PANEL/REFRESH_
        // handling below (see isWorkflowModuleAction's comment for why).
        if (isWorkflowModuleAction(a) && t.endsWith('_PANEL')) {
          if (mid) {
            workflowTurnIdsRef.current.add(mid);
            if (workflowTurnIdsRef.current.size > 200) workflowTurnIdsRef.current = new Set([mid]);
          }
          optsRef.current.onOpenWorkflowPanel?.(a.record_id ?? undefined, a.title, a.caption, mid);
          continue;
        }
        if (isWorkflowModuleAction(a) && t.startsWith('REFRESH_')) {
          if (mid) {
            workflowTurnIdsRef.current.add(mid);
            if (workflowTurnIdsRef.current.size > 200) workflowTurnIdsRef.current = new Set([mid]);
          }
          optsRef.current.onRefreshWorkflow?.(a.record_id ?? undefined);
          continue;
        }
        if (t === NAVIGATE_ROUTE) {
          if (a.path) optsRef.current.onNavigateRoute?.(a.path);
          continue;
        }
        if (t === SWITCH_TASK_TAB) {
          if (a.tab) optsRef.current.onSwitchTaskTab?.(a.tab);
          continue;
        }
        if (t === SWITCH_MEETING_TAB) {
          if (a.tab) optsRef.current.onSwitchMeetingTab?.(a.tab);
          continue;
        }
        if (t === REFRESH_TASK_COMMENTS) {
          if (a.record_id) optsRef.current.onRefreshTaskComments?.(a.record_id);
          continue;
        }
        if (t === REFRESH_TASK_DETAIL) {
          if (a.record_id) optsRef.current.onRefreshTaskDetail?.(a.record_id);
          continue;
        }
        if (t === REFRESH_MEETING_DETAIL) {
          if (a.record_id) optsRef.current.onRefreshMeetingDetail?.(a.record_id);
          continue;
        }
        if (t === REFRESH_PROPERTY_DETAIL) {
          if (a.record_id) optsRef.current.onRefreshPropertyDetail?.(a.record_id);
          continue;
        }
        if (t === NAVIGATE_CALENDAR) {
          optsRef.current.onNavigateCalendar?.({
            direction: a.direction,
            unit: a.unit,
            view: a.view,
            date: a.date,
          });
          continue;
        }
        // This turn already opened/refreshed the Workflow surface — an unrelated module's read
        // (e.g. get_meetings called right after execute_workflow_transition) must not steal the
        // workspace surface away from the engagement the user was just looking at.
        const suppressSurfaceSwitch = !!mid && workflowTurnIdsRef.current.has(mid) && a.module !== 'workflow';
        routeNavigation(a);
        if (!suppressSurfaceSwitch && (t.includes('FILTER') || t.endsWith('_PANEL')) && a.module && !applied.has(a.module)) {
          applied.add(a.module);
          // Opening a panel list view should collapse ANY currently open detail card, regardless
          // of module — e.g. task detail open → "show calendar" must clear it. Passing undefined
          // triggers the "clear all open cards" branch in useChatWorkspace onOpenCard.
          optsRef.current.onOpenCard?.(undefined as unknown as SamModule, null);
          optsRef.current.onApplyFilter?.(a.module, a.filter ?? {}, a.facets, a.title, a.caption, mid, a.record_id ?? null, { title: a.view_title, caption: a.view_caption });
          // Optional inline chat card (config-gated) — only for reads carrying a title/caption.
          if (a.title || a.caption) {
            optsRef.current.onActionCard?.(a.module, a.filter ?? {}, a.title, a.caption, a.record_id ?? null);
          }
        }
        if (!suppressSurfaceSwitch && t.startsWith('REFRESH_') && a.module) optsRef.current.onRefresh?.(a.module, mid);
      }
    };

    // Listen on both the shared `agent:action` channel (text / PTT / realtime CRM tools
    // via publish_action) and the realtime-prefixed `agent_realtime:action` channel
    // (actions emitted by the realtime session's _drain_actions loop).
    socketService.on('agent:action', onAction);
    socketService.on('agent_realtime:action', onAction);
    return () => {
      socketService.off('agent:action', onAction);
      socketService.off('agent_realtime:action', onAction);
    };
  }, []);
}
